import { useCallback, useEffect, useState } from "react";
import { Sparkles, Info } from "lucide-react";
import { Select } from "antd";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_COMPETENCY_RATINGS } from "../../constants/api";

/**
 * Competency assessment on an appraisal.
 *
 * Shown only when the cycle opted in and rows were seeded, so cycles created
 * before this feature render exactly as they did.
 *
 * The result is reported here and never folded into the final score: that
 * number is a weighted result against agreed KPI targets, and mixing an
 * unweighted behavioural judgement into it would change what every past score
 * meant. The gap against the expected level is the output instead.
 *
 * `role` is "employee" or "rm"; anything else reads only.
 */
const CompetencyPanel = ({ appraisal, role, readOnly }) => {
    const { fetch, update } = useApiMethods();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState({});
    const [openAnchors, setOpenAnchors] = useState(null);

    const appraisalId = appraisal?.id;
    const isEmployee = role === "employee";
    const isRM = role === "rm";
    const scaleMax = appraisal?.cycle_rating_scale_max || 5;
    const canEdit = (isEmployee || isRM) && !readOnly && !appraisal?.is_locked;

    const load = useCallback(async () => {
        if (!appraisalId) return;
        setLoading(true);
        try {
            const res = await fetch(APPRAISAL_COMPETENCY_RATINGS, { appraisal_id: appraisalId });
            setRows(res?.status === "success" ? res.data || [] : []);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appraisalId]);

    useEffect(() => { load(); }, [load]);

    // Nothing was seeded for this appraisal — the cycle does not run
    // competencies, so the panel stays out of the way entirely.
    if (!loading && rows.length === 0) return null;

    const patch = (id, field, value) => {
        setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
        setDirty((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));
    };

    const save = async () => {
        const ratings = Object.entries(dirty).map(([id, fields]) => ({ id: Number(id), ...fields }));
        if (!ratings.length) {
            ToastMsg("error", "Nothing to save");
            return;
        }
        setSaving(true);
        try {
            const res = await update(APPRAISAL_COMPETENCY_RATINGS, {
                appraisal_id: appraisalId, ratings,
            });
            if (res?.status === "success") {
                ToastMsg("success", "Competencies saved");
                setRows(res.data || []);
                setDirty({});
            } else {
                ToastMsg("error", res?.message || "Could not save");
                await load();
            }
        } catch (_) {
            ToastMsg("error", "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const options = Array.from({ length: scaleMax }, (_, i) => ({ value: i + 1, label: `${i + 1}` }));

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
            <h3 className="text-base font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Competencies
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
                How the work gets done, as against whether the targets were hit. Rated
                separately and reported on its own &mdash; competency scores are not added
                into the final appraisal score.
            </p>

            {loading ? (
                <div className="text-center py-6 text-muted-foreground text-sm">Loading…</div>
            ) : (
                <>
                    <div className="space-y-3">
                        {rows.map((r) => {
                            const gap = r.gap;
                            return (
                                <div key={r.id} className="border border-border rounded-lg p-3">
                                    <div className="flex items-start justify-between gap-2 flex-wrap">
                                        <div className="min-w-[180px] flex-1">
                                            <div className="font-medium text-sm flex items-center gap-1">
                                                {r.name}
                                                {r.anchors && Object.keys(r.anchors).length > 0 && (
                                                    <button type="button"
                                                        title="What each level means"
                                                        onClick={() => setOpenAnchors(openAnchors === r.id ? null : r.id)}
                                                        className="text-muted-foreground hover:text-foreground">
                                                        <Info className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {String(r.category || "").replace("_", " ")}
                                                {r.expected_level ? ` · expected ${r.expected_level}` : ""}
                                            </div>
                                        </div>
                                        {gap !== null && gap !== undefined ? (
                                            <span className={cn("text-xs px-2 py-0.5 rounded-full",
                                                gap < 0
                                                    ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                                    : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300")}>
                                                {gap === 0 ? "At expected" : gap > 0 ? `+${gap} above` : `${gap} below`}
                                            </span>
                                        ) : !r.expected_level ? (
                                            // The framework set no bar to measure against. Without this the
                                            // chip just vanishes, which reads as a rating that did not save
                                            // rather than a framework that needs configuring.
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                                title="Set an expected level on this competency in the framework to see the gap">
                                                No expected level
                                            </span>
                                        ) : null}
                                    </div>

                                    {openAnchors === r.id && (
                                        <div className="mt-2 rounded-md bg-gray-50 dark:bg-gray-900/40 p-2 space-y-0.5">
                                            {Object.entries(r.anchors)
                                                .sort((a, b) => Number(a[0]) - Number(b[0]))
                                                .map(([level, text]) => (
                                                    <p key={level} className="text-xs">
                                                        <span className="font-medium">{level}:</span> {text}
                                                    </p>
                                                ))}
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                                        <div>
                                            <label className="block text-xs font-medium mb-1">Self</label>
                                            <div className="flex gap-2">
                                                <Select className="w-24" allowClear placeholder="—"
                                                    disabled={!canEdit || !isEmployee}
                                                    value={r.self_rating ?? undefined}
                                                    onChange={(v) => patch(r.id, "self_rating", v ?? null)}
                                                    options={options} />
                                                <Input className="flex-1" placeholder="Comment"
                                                    disabled={!canEdit || !isEmployee}
                                                    value={r.self_comment || ""}
                                                    onChange={(e) => patch(r.id, "self_comment", e.target.value)} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1">Manager</label>
                                            <div className="flex gap-2">
                                                <Select className="w-24" allowClear placeholder="—"
                                                    disabled={!canEdit || !isRM}
                                                    value={r.rm_rating ?? undefined}
                                                    onChange={(v) => patch(r.id, "rm_rating", v ?? null)}
                                                    options={options} />
                                                <Input className="flex-1" placeholder="Comment"
                                                    disabled={!canEdit || !isRM}
                                                    value={r.rm_comment || ""}
                                                    onChange={(e) => patch(r.id, "rm_comment", e.target.value)} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {canEdit && Object.keys(dirty).length > 0 && (
                        <div className="flex justify-end mt-3">
                            <Button size="sm" variant="brand" disabled={saving} onClick={save}>
                                {saving ? "Saving…" : "Save competencies"}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CompetencyPanel;
